import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import Moveable, { MoveableEventsParameters, MoveableOptions } from 'moveable';

type MoveableTarget = HTMLElement;

@Component({
  selector: 'lib-zoneless-moveable',
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ZonelessMoveableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() draggable: MoveableOptions['draggable'];
  @Input() resizable: MoveableOptions['resizable'];
  @Input() scalable: MoveableOptions['scalable'];
  @Input() rotatable: MoveableOptions['rotatable'];
  @Input() edge: MoveableOptions['edge'];
  @Input() keepRatio: MoveableOptions['keepRatio'];
  @Input() origin: MoveableOptions['origin'];
  @Input() pinchable: MoveableOptions['pinchable'];
  @Input() target: MoveableOptions['target'];
  @Input() throttleDrag: MoveableOptions['throttleDrag'];
  @Input() throttleResize: MoveableOptions['throttleResize'];
  @Input() throttleRotate: MoveableOptions['throttleRotate'];
  @Input() throttleScale: MoveableOptions['throttleScale'];
  @Input() snappable: MoveableOptions['snappable'];
  @Input() elementGuidelines: MoveableOptions['elementGuidelines'];
  @Input() snapDirections: MoveableOptions['snapDirections'];
  @Input() elementSnapDirections: MoveableOptions['elementSnapDirections'];
  @Input() verticalGuidelines: MoveableOptions['verticalGuidelines'];
  @Input() horizontalGuidelines: MoveableOptions['horizontalGuidelines'];
  @Input() snapDistFormat: MoveableOptions['snapDistFormat'];
  @Input() bounds: MoveableOptions['bounds'];
  @Input() container: MoveableTarget | null = null;

  @Output() dragStart = new EventEmitter<
    MoveableEventsParameters['dragStart']
  >();
  @Output() drag = new EventEmitter<MoveableEventsParameters['drag']>();
  @Output() dragEnd = new EventEmitter<MoveableEventsParameters['dragEnd']>();
  @Output() resize = new EventEmitter<MoveableEventsParameters['resize']>();
  @Output() resizeEnd = new EventEmitter<
    MoveableEventsParameters['resizeEnd']
  >();
  @Output() rotateStart = new EventEmitter<
    MoveableEventsParameters['rotateStart']
  >();
  @Output() rotate = new EventEmitter<MoveableEventsParameters['rotate']>();
  @Output() rotateEnd = new EventEmitter<
    MoveableEventsParameters['rotateEnd']
  >();

  private moveable: Moveable | null = null;
  private appliedOptions: MoveableOptions = {};

  constructor(private readonly zone: NgZone) {}

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.appliedOptions = this.createOptions();
      this.moveable = new Moveable(
        this.container ?? document.body,
        this.appliedOptions,
      );
      this.registerEvents(this.moveable);
    });
  }

  ngOnChanges(): void {
    if (!this.moveable) {
      return;
    }

    const options = this.createOptions();
    const changedOptions: Partial<MoveableOptions> = {};
    const collectChangedOption = <K extends keyof MoveableOptions>(key: K) => {
      if (options[key] !== this.appliedOptions[key]) {
        changedOptions[key] = options[key];
      }
    };
    (Object.keys(options) as Array<keyof MoveableOptions>).forEach(
      collectChangedOption,
    );
    this.appliedOptions = options;

    if (Object.keys(changedOptions).length === 0) {
      return;
    }

    // Moveable schedules a render timer in setState. Keep that timer outside
    // Angular as well as the gesture listeners installed in ngOnInit.
    this.zone.runOutsideAngular(() => this.moveable?.setState(changedOptions));
  }

  ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => this.moveable?.destroy());
    this.moveable = null;
  }

  private createOptions(): MoveableOptions {
    return {
      draggable: this.draggable,
      resizable: this.resizable,
      scalable: this.scalable,
      rotatable: this.rotatable,
      edge: this.edge,
      keepRatio: this.keepRatio,
      origin: this.origin,
      pinchable: this.pinchable,
      target: this.target,
      throttleDrag: this.throttleDrag,
      throttleResize: this.throttleResize,
      throttleRotate: this.throttleRotate,
      throttleScale: this.throttleScale,
      snappable: this.snappable,
      elementGuidelines: this.elementGuidelines,
      snapDirections: this.snapDirections,
      elementSnapDirections: this.elementSnapDirections,
      verticalGuidelines: this.verticalGuidelines,
      horizontalGuidelines: this.horizontalGuidelines,
      snapDistFormat: this.snapDistFormat,
      bounds: this.bounds,
    };
  }

  private registerEvents(moveable: Moveable): void {
    moveable.on('dragStart', (event) => this.dragStart.emit(event));
    moveable.on('drag', (event) => this.drag.emit(event));
    moveable.on('dragEnd', (event) =>
      this.zone.run(() => this.dragEnd.emit(event)),
    );
    moveable.on('resize', (event) => this.resize.emit(event));
    moveable.on('resizeEnd', (event) =>
      this.zone.run(() => this.resizeEnd.emit(event)),
    );
    moveable.on('rotateStart', (event) => this.rotateStart.emit(event));
    moveable.on('rotate', (event) => this.rotate.emit(event));
    moveable.on('rotateEnd', (event) =>
      this.zone.run(() => this.rotateEnd.emit(event)),
    );
  }
}
